import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, ArrowLeft, ArrowRight, Sparkles, ShieldCheck, Microscope, Activity, Terminal
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import ScientificSkillsSuiteModal from './ScientificSkillsSuiteModal';

interface ScienceCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const SCIENCE_AGENT_IDS = [
  'deep-research',
  'scientific-methodology-goal-architect',
  'science-grant-proposal-writer',
  'science-paper-peer-reviewer',
  'science-patent-prior-art-analyst',
  'science-phd-dissertation-auditor',
  'science-data-statistician-r-python',
  'nazavo-accreditation-auditor',
  'syllabus-curriculum-generator',
  'test-bank-constructor',
  'scopus-wos-paper-auditor',
  'nrfu-grant-proposal-builder',
  'phd-dissertation-compliance',
  'thesis-formatter-dstu',
  'patent-claims-builder',
  'iso-17025-lab-accreditor',
  'nddkr-report-constructor',
  'r-python-data-statistician',
  'tech-transfer-commercializer',
  'dpp-product-passport-validator'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <GraduationCap className={className} />;
  return <IconComponent className={className} />;
};

export default function ScienceCenterHubView({
  onLaunchAgent,
  onBack,
  language, currency = 'UAH'}: ScienceCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const [pricing, setPricing] = useState<Record<string, { price: number; limit: number; durationDays: number; singlePrice: number }>>({});
  const [isSkillsModalOpen, setIsSkillsModalOpen] = useState(false);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          setPricing(data);
        }
      } catch (err) {
        console.error('Failed to fetch science pricing:', err);
      }
    };
    fetchPricing();
  }, []);

  const agents = agentsConfig.filter((a: any) => SCIENCE_AGENT_IDS.includes(a.id));

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-slate-800 rounded-xl p-4 text-white">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded-md">
            {isPl ? '🎓 18 Modułów Naukowych & B+R' : isUk ? '🎓 18 Науково-Академічних Експертів' : '🎓 18 Science & R&D Experts'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded-md flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-blue-400" />
            {isPl ? 'Deep Research • Scopus • Horizon Europe' : isUk ? 'Deep Research • Scopus • Horizon • НАЗЯВО' : 'Deep Research & Academic Standards'}
          </span>
        </div>
      </div>

      <div className="relative bg-gradient-to-r from-slate-950 via-purple-950 to-slate-900 border border-purple-500/40 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
              {isPl ? 'CENTRUM BADAWCZE, B+R & AKADEMIA' : isUk ? 'НАУКОВО-ДОСЛІДНИЙ ЦЕНТР, R&D ТА АКАДЕМІЯ' : 'SCIENCE, R&D & ACADEMIC RESEARCH'}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-wide text-white">
            {isPl ? '🎓 CENTRUM NAUKOWE, B+R & INNOWACJE' : isUk ? '🎓 НАУКОВИЙ ЦЕНТР, R&D & ІННОВАЦІЇ' : '🎓 SCIENCE, R&D & INNOVATION HUB'}
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Wielomodułowy pakiet dla naukowców i sektora B+R: Centrum Innowacji 2026 (Canvas, Podcast, MCP), silnik Deep Research, wnioski grantowe (Horizon Europe/NCBR), recenzowanie artykułów Scopus/WoS (Q1-Q4), analiza patentowa Prior Art oraz przetwarzanie danych w R/Python.'
              : isUk
              ? 'Мультиагентний пакет для науковців, R&D та інноваторів: Центр Інновацій 2026 (Canvas, ШІ-подкасти, MCP-конектори, Change Detection), флагманський Deep Research, гранти Horizon Europe / НФДМУ, рецензування Scopus/WoS, патенти та обробка даних в R/Python.'
              : 'Multi-agent suite for researchers and R&D: 2026 Innovation Hub (Canvas, Podcasts, MCP), flagship Deep Research engine, grant writing (Horizon Europe), Scopus/WoS peer-reviewing, patent prior art analysis, and statistical data processing in R/Python.'}
          </p>
        </div>
      </div>

      {/* FEATURED R&D & INNOVATION SUITE: ЦЕНТР ІННОВАЦІЙ 2026 */}
      <div className="relative bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-900 border-2 border-indigo-500/70 hover:border-indigo-400 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-indigo-500/20 flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden font-mono transition-all">
        <div className="absolute right-0 top-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="space-y-3 z-10 max-w-3xl">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded shadow-xs">
              🚀 NEW 2026 INNOVATION & R&D SUITE
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
              {isPl ? '8 Autonomicznych Modułów' : isUk ? '8 Автономних Модулів Інновацій' : '8 Autonomous Modules'}
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded">
              {isPl ? 'Canvas • Podcast • MCP • Satellites' : isUk ? 'Canvas • ШІ-Подкасти • MCP • Супутники' : 'Canvas • Podcasts • MCP'}
            </span>
          </div>

          <h2 className="text-lg sm:text-2xl font-extrabold uppercase text-white flex items-center gap-2 tracking-tight">
            <Sparkles className="w-6 h-6 text-indigo-400 shrink-0 animate-pulse" />
            <span>{isPl ? 'CENTRUM INNOWACJI 2026 (Canvas, Podcast, MCP, Change Detection)' : isUk ? 'ЦЕНТР ІННОВАЦІЙ 2026 (Canvas, Podcast, MCP, Change Detection)' : '2026 INNOVATION HUB (Canvas, Podcast, MCP, Change Detection)'}</span>
          </h2>

          <p className="text-xs text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Interaktywny Canvas edycji raportów, podcasty audio, konektory MCP (1C/BAS, Google Drive), satelitarny monitoring zmian "Przed/Po", Kolegium Ekspertów oraz auto-monitory.'
              : isUk 
              ? 'Інтерактивний Canvas редагування звітів, ШІ-подкасти, MCP-конектори (1С/BAS, Google Drive), супутниковий моніторинг змін "До/Після", Експертна Колегія та авто-монітори.'
              : 'Interactive Canvas report editor, AI audio podcasts, MCP connectors (1C/BAS, Google Drive), Satellite Change Detection, and Multi-agent Debate.'}
          </p>
        </div>

        <button
          onClick={() => onLaunchAgent('innovation-hub')}
          className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-indigo-600/40 flex items-center justify-center gap-2.5 cursor-pointer shrink-0 z-10 hover:scale-[1.02] border border-indigo-400/30"
        >
          <span>{isPl ? '🔮 OTWÓRZ CENTRUM INNOWACJI 2026' : isUk ? '🔮 ВІДКРИТИ ЦЕНТР ІННОВАЦІЙ 2026' : '🔮 OPEN 2026 INNOVATION HUB'}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* 🔬 FEATURED SCIENTIFIC AGENT SKILLS (177 SKILLS & CONTEXT-MODE MCP) */}
      <div className="relative bg-gradient-to-r from-purple-950 via-slate-950 to-indigo-950 border-2 border-purple-500/70 hover:border-purple-400 rounded-2xl p-6 sm:p-8 shadow-2xl shadow-purple-500/20 flex flex-col md:flex-row md:items-center justify-between gap-6 overflow-hidden font-mono transition-all">
        <div className="absolute right-0 top-0 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="space-y-3 z-10 max-w-3xl">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded shadow-xs">
              🔬 K-DENSE SCIENTIFIC SUITE
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
              {isPl ? '177 Autonomicznych Modułów' : isUk ? '177 Автономних Наукових Модулів' : '177 Autonomous Scientific Skills'}
            </span>
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded flex items-center gap-1">
              <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
              <span>MCP Context-Mode: Online</span>
            </span>
          </div>

          <h2 className="text-lg sm:text-2xl font-extrabold uppercase text-white flex items-center gap-2 tracking-tight">
            <Microscope className="w-6 h-6 text-purple-400 shrink-0" />
            <span>{isPl ? '177 NAUKOWYCH SKILLS & MCP CONTEXT-MODE' : isUk ? '177 НАУКОВИХ SKILLS ТА СЕРВЕР MCP CONTEXT-MODE' : '177 SCIENTIFIC SKILLS & MCP CONTEXT-MODE'}</span>
          </h2>

          <p className="text-xs text-slate-300 leading-relaxed font-mono">
            {isPl
              ? 'Pełna biblioteka procedur naukowych (Genomika, Chemia, Bioinformatyka, Astrofizyka, GUM, Scopus) z ochroną okna kontekstowego i izolacją tokenów przez serwer MCP Context-Mode.'
              : isUk 
              ? 'Повна бібліотека процедурних наукових стандартів (Біоінформатика, Хімія, Фармакологія, Астрофізика, ДЗЗ, GUM, Scopus Q1) із прямим доступом до 100+ баз даних та оптимізацією токенів Context-Mode.'
              : 'Full library of procedural scientific standards (Bioinformatics, Chemistry, Earth Observation, Astrophysics, Scopus Q1) with 100+ databases and MCP context isolation.'}
          </p>
        </div>

        <button
          onClick={() => setIsSkillsModalOpen(true)}
          className="px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg shadow-purple-600/40 flex items-center justify-center gap-2.5 cursor-pointer shrink-0 z-10 hover:scale-[1.02] border border-purple-400/30"
        >
          <span>{isPl ? '🔬 PRZEGLĄDAJ 177 SKILLS & MCP' : isUk ? '🔬 ПЕРЕГЛЯНУТИ 177 SKILLS & MCP' : '🔬 EXPLORE 177 SKILLS & MCP'}</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent: any) => {
          const agentPricing = pricing[agent.id];
          const priceVal = agentPricing ? agentPricing.price : 490;
          const limitVal = agentPricing ? agentPricing.limit : 30;
          const durationDaysVal = agentPricing ? agentPricing.durationDays : 30;
          const singlePriceVal = agentPricing ? agentPricing.singlePrice : 25;

          return (
            <div
              key={agent.id}
              className="bg-slate-950 border border-purple-500/30 hover:border-purple-400 rounded-2xl p-6 flex flex-col justify-between gap-6 transition-all hover:scale-[1.01] shadow-xl group"
            >
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-2.5 py-1 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
                    {getLocalizedAgentCategory(agent, language)}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
                    ✨ {agent.model === 'gemini-3.7-flash' ? 'Gemini 3.7 Flash' : 'Gemini 3.7 Flash'}
                  </span>
                  <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded font-mono">
                    {formatPriceWithCurrency(priceVal, currency)} / {limitVal} {isUk ? (limitVal === 1 ? 'запит' : 'запитів') : isPl ? 'zapytań' : 'req.'} ({durationDaysVal} {isUk ? 'дн.' : isPl ? 'dni' : 'days'})
                  </span>
                  {singlePriceVal > 0 && (
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded font-mono">
                      {isUk ? `Разово: ${formatPriceWithCurrency(singlePriceVal, currency)}` : isPl ? `Pojedynczo: ${formatPriceWithCurrency(singlePriceVal, currency)}` : `Single: ${formatPriceWithCurrency(singlePriceVal, currency)}`}
                    </span>
                  )}
                  <span className="px-2 py-0.5 text-[9px] font-bold text-amber-300 bg-amber-950/60 rounded border border-amber-500/30">
                    {isPl ? '🎁 1. start ZA DARMO' : isUk ? '🎁 1-й запуск БЕЗКОШТОВНО' : '🎁 1st Run FREE'}
                  </span>
                </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-950/80 border border-purple-500/30 rounded-xl text-purple-400 shrink-0 group-hover:text-purple-300 transition-colors">
                  <AgentIcon name={agent.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-white uppercase group-hover:text-purple-300 transition-colors leading-snug">
                    {getLocalizedAgentName(agent, language)}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 font-mono leading-relaxed line-clamp-3">
                    {getLocalizedAgentDesc(agent, language)}
                  </p>
                </div>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-slate-800">
                {getLocalizedAgentFeatures(agent, language).map((feat: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-2 text-[11px] text-slate-300 font-mono">
                    <ShieldCheck className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                    <span>{feat}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-slate-800">
              <button
                onClick={() => onLaunchAgent(agent.id === 'deep-research' ? 'research' : agent.id)}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>{isPl ? '🚀 Otwórz Moduł Naukowy' : isUk ? '🚀 Відкрити Науковий Модуль' : '🚀 Launch Science Module'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })}
      </div>

      <ScientificSkillsSuiteModal
        isOpen={isSkillsModalOpen}
        onClose={() => setIsSkillsModalOpen(false)}
        language={language}
        onSelectSkill={(skillName) => {
          setIsSkillsModalOpen(false);
          onLaunchAgent('scientific-methodology-goal-architect');
        }}
      />
    </div>
  );
}

